import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Interface pour un chantier à démarrer automatiquement
interface ChantierADemarrer {
  id: string;
  nom_chantier: string;
  date_debut_prevue: string;
  date_debut_reelle: string | null;
  client: string;
}

// Options de réponse de l'utilisateur
export type AutoStartAction = 'confirmer' | 'repousser' | 'suspendre' | 'garder_planifie';

interface UseAutoStartChantiersResult {
  // Chantier actuellement affiché dans le modal
  chantierCourant: ChantierADemarrer | null;
  // Modal ouvert ou fermé
  isModalOpen: boolean;
  // Nombre total de chantiers à traiter
  totalChantiers: number;
  // Index du chantier courant (0-based)
  currentIndex: number;
  // Traiter l'action de l'utilisateur
  handleAction: (action: AutoStartAction, nouvelleDate?: string) => Promise<void>;
  // Passer au chantier suivant ou fermer le modal
  handleNext: () => void;
  // Rafraîchir les données après modification
  refreshData: () => void;
  // Indiquer si on charge
  isProcessing: boolean;
}

// Intervalle de polling en millisecondes (5 minutes)
const POLLING_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Hook pour détecter les chantiers "planifié" dont la date de début est atteinte
 * et les passer automatiquement en "en_cours" avec notification à l'utilisateur.
 *
 * Comportement :
 * 1. À la connexion ET toutes les 5 minutes, cherche les chantiers planifiés avec date_debut_prevue <= aujourd'hui
 * 2. Les passe en "en_cours" automatiquement
 * 3. Affiche un modal pour chaque chantier avec 4 options :
 *    - Confirmer le démarrage (rester en_cours)
 *    - Repousser la date (revenir en planifie)
 *    - Suspendre (ouvrir le modal de suspension)
 *    - Garder en Planifié (revenir au statut précédent)
 *
 * Polling temps réel :
 * - Vérifie toutes les 5 minutes si de nouveaux chantiers doivent démarrer
 * - Utilise la date du jour (pas l'heure) pour la transition de statut
 * - Les coûts horaires sont gérés côté SQL (compute_chantier_metrics)
 */
export function useAutoStartChantiers(
  entrepriseId: string | null,
  onRefreshData?: () => void
): UseAutoStartChantiersResult {
  const [chantiersADemarrer, setChantiersADemarrer] = useState<ChantierADemarrer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // IDs des chantiers déjà traités dans cette session (évite de re-notifier)
  const [processedChantierIds, setProcessedChantierIds] = useState<Set<string>>(new Set());

  // Fonction de vérification des chantiers à démarrer
  const checkAndStartChantiers = useCallback(async () => {
    if (!entrepriseId) return;

    try {
      const today = new Date().toISOString().split('T')[0];

      // Trouver les chantiers planifiés dont la date de début est atteinte
      const { data: chantiers, error: fetchError } = await supabase
        .from('chantiers')
        .select('id, nom_chantier, date_debut_prevue, date_debut_reelle, client')
        .eq('entreprise_id', entrepriseId)
        .eq('etat_chantier', 'planifie')
        .lte('date_debut_prevue', today)
        .order('date_debut_prevue', { ascending: true });

      if (fetchError) {
        console.error('Erreur lors de la recherche des chantiers planifiés:', fetchError);
        return;
      }

      if (!chantiers || chantiers.length === 0) {
        return;
      }

      // Filtrer les chantiers déjà traités dans cette session
      const nouveauxChantiers = chantiers.filter(c => !processedChantierIds.has(c.id));

      if (nouveauxChantiers.length === 0) {
        return;
      }

      // Mettre à jour tous ces chantiers en "en_cours"
      for (const chantier of nouveauxChantiers) {
        const updateData: { etat_chantier: string; date_debut_reelle?: string } = {
          etat_chantier: 'en_cours'
        };

        // Définir la date de début réelle seulement si pas déjà définie
        if (!chantier.date_debut_reelle) {
          updateData.date_debut_reelle = today;
        }

        const { error: updateError } = await supabase
          .from('chantiers')
          .update(updateData)
          .eq('id', chantier.id);

        if (updateError) {
          console.error(`Erreur passage en cours chantier ${chantier.id}:`, updateError);
        }
      }

      // Marquer ces chantiers comme traités
      setProcessedChantierIds(prev => {
        const newSet = new Set(prev);
        nouveauxChantiers.forEach(c => newSet.add(c.id));
        return newSet;
      });

      // Stocker les chantiers pour le modal
      setChantiersADemarrer(nouveauxChantiers);
      setCurrentIndex(0);
      setIsModalOpen(true);

    } catch (error) {
      console.error('Erreur checkAndStartChantiers:', error);
    }
  }, [entrepriseId, processedChantierIds]);

  // Vérification initiale au montage + polling toutes les 5 minutes
  useEffect(() => {
    if (!entrepriseId) return;

    // Vérification initiale
    checkAndStartChantiers();

    // Polling toutes les 5 minutes
    const intervalId = setInterval(() => {
      checkAndStartChantiers();
    }, POLLING_INTERVAL_MS);

    // Nettoyage à la désinscription
    return () => {
      clearInterval(intervalId);
    };
  }, [entrepriseId, checkAndStartChantiers]);

  // Chantier actuellement affiché
  const chantierCourant = chantiersADemarrer[currentIndex] || null;

  // Passer au chantier suivant ou fermer le modal
  const handleNext = useCallback(() => {
    if (currentIndex < chantiersADemarrer.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsModalOpen(false);
      // Rafraîchir les données après traitement de tous les chantiers
      onRefreshData?.();
    }
  }, [currentIndex, chantiersADemarrer.length, onRefreshData]);

  // Traiter l'action de l'utilisateur
  const handleAction = useCallback(async (action: AutoStartAction, nouvelleDate?: string) => {
    if (!chantierCourant) return;

    setIsProcessing(true);

    try {
      switch (action) {
        case 'confirmer':
          // Le chantier est déjà en_cours, rien à faire
          toast.success(`Chantier "${chantierCourant.nom_chantier}" démarré`);
          break;

        case 'repousser':
          if (!nouvelleDate) {
            toast.error('Veuillez sélectionner une nouvelle date');
            setIsProcessing(false);
            return;
          }
          // Revenir en planifié avec nouvelle date
          const { error: repousserError } = await supabase
            .from('chantiers')
            .update({
              etat_chantier: 'planifie',
              date_debut_prevue: nouvelleDate,
              date_debut_reelle: null // Annuler la date réelle
            })
            .eq('id', chantierCourant.id);

          if (repousserError) throw repousserError;
          toast.success(`Date de début repoussée au ${formatDateFr(nouvelleDate)}`);
          break;

        case 'garder_planifie':
          // Revenir en planifié sans changer la date
          const { error: planifieError } = await supabase
            .from('chantiers')
            .update({
              etat_chantier: 'planifie',
              date_debut_reelle: null // Annuler la date réelle
            })
            .eq('id', chantierCourant.id);

          if (planifieError) throw planifieError;
          toast.info(`Chantier "${chantierCourant.nom_chantier}" maintenu en planifié`);
          break;

        case 'suspendre':
          // Ce cas sera géré par le composant parent qui ouvrira ModalSuspensionChantier
          // On ferme juste ce modal, le parent gère la suite
          break;
      }

      // Passer au suivant
      handleNext();

    } catch (error) {
      console.error('Erreur handleAction:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setIsProcessing(false);
    }
  }, [chantierCourant, handleNext]);

  // Fonction pour forcer un rafraîchissement
  const refreshData = useCallback(() => {
    onRefreshData?.();
  }, [onRefreshData]);

  return {
    chantierCourant,
    isModalOpen,
    totalChantiers: chantiersADemarrer.length,
    currentIndex,
    handleAction,
    handleNext,
    refreshData,
    isProcessing,
  };
}

// Helper : formater une date en français (JJ/MM/AAAA)
function formatDateFr(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}
